import { Clear } from '@mui/icons-material'
import { Box, Checkbox, Popover, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import { bindPopover } from 'material-ui-popup-state'
import React, { MutableRefObject, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useCreateWorkforceScheduleMutation,
  useLazyGetWorkforceSchedulesQuery,
  WorkforceScheduleModify,
} from '../../../store/redux/reducers/workforceScheduleSlice'
import { WorkforceSchedule } from '../../../store/types'
import ConfirmationDialog from '../../Dialog/ConfirmationDialog'
import { useAppSelector, useNotification } from '../../../store/redux/hooks'
import DateFormat from '../../../store/types/enums/DateFormat'
import { SquareButton } from '../../../styles/ButtonStyles'
import { PopupState } from 'material-ui-popup-state/hooks'
import Demand from '../../../store/types/models/Demand'
import {
  useCreateDemandMutation,
  useEditDemandMutation,
  useLazyGetDemandsQuery,
} from '../../../store/redux/reducers/demandSlice'
import defaultProps from '../../../config/defaultProps'
import { getCurrentUser } from '../../../store/redux/reducers/currentUserSlice'
import { createErrorMessage } from '../../../store/utils'
import ConfirmButton from '../../Button/ConfirmButton'

type DuplicateForMonthPopupProps = {
  monthToDuplicateData: WorkforceSchedule[]
  demandToDuplicateData: Demand[]
  targetRef: MutableRefObject<null>
  popupState: PopupState
  month: Dayjs
}

const DuplicateForMonthPopup = ({
  monthToDuplicateData,
  demandToDuplicateData,
  targetRef,
  popupState,
  month,
}: DuplicateForMonthPopupProps) => {
  const { t } = useTranslation()
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(month.add(1, 'month'))
  const [trigger] = useLazyGetWorkforceSchedulesQuery()
  const [demandTrigger] = useLazyGetDemandsQuery()
  const [isOverwriteDataDialogOpen, setIsOverwriteDataDialogOpen] = useState(false)
  const [createWorkforce] = useCreateWorkforceScheduleMutation()
  const { displayNotification } = useNotification()
  const { leaderId } = useAppSelector(state => state.leaderId)
  const [loading, setLoading] = useState<boolean>(false)
  const currentUser = useAppSelector(getCurrentUser)
  const [isSelectedDemandCopy, setIsSelectedDemandCopy] = useState(false)

  const [createDemand] = useCreateDemandMutation()
  const [editDemand] = useEditDemandMutation()

  const handleApplyButtonClick = async () => {
    if (!selectedDate) return

    if (selectedDate.isBefore(dayjs(), 'month')) {
      displayNotification({ message: t('page.projectDistribution.cannotEditBeforeMonth'), type: 'error' })
      return
    }

    if (monthToDuplicateData.length === 0) {
      return
    }

    const fetchResult = await trigger({
      id: leaderId!!,
      props: {
        date: selectedDate.format(DateFormat.YearMonth),
      },
    })

    if (fetchResult.isSuccess && fetchResult.data.length > 0) {
      setIsOverwriteDataDialogOpen(true)
    } else {
      setLoading(true)
      await duplicateData().finally(() => setLoading(false))
    }
  }

  const duplicateDemandData = async () => {
    if (!selectedDate) return

    const fetchedDemandData = await demandTrigger({
      ...defaultProps,
      startDate: selectedDate.utc(true).format(DateFormat.YearMonthDay),
      'submitter.id': leaderId || currentUser?.id,
      state: 'INTERNAL',
      completed: false,
    })

    try {
      if (fetchedDemandData.data?.content)
        for (const demand of fetchedDemandData.data?.content) {
          await editDemand({
            id: demand.id,
            demand: {
              numberOfPeople: demand.numberOfPeople,
              technologies: demand.technologies,
              projectId: demand.project.id,
              submitterId: demand.submitter.id,
              startDate: dayjs(demand.startDate).utc(true).startOf('month'),
              state: demand.state,
              description: demand.description,
              completed: true,
              version: demand.version,
            },
          }).unwrap()
        }
    } catch (error) {
      let errorMessage = createErrorMessage(error)
      displayNotification({ message: `${t('message.somethingWentWrong')} ${errorMessage}`, type: 'error' })
    }

    try {
      for (const demand of demandToDuplicateData) {
        await createDemand({
          numberOfPeople: demand.numberOfPeople,
          technologies: demand.technologies,
          projectId: demand.project.id,
          submitterId: demand.submitter.id,
          startDate: dayjs(selectedDate).utc(true).startOf('month'),
          state: demand.state,
          description: demand.description,
          completed: false,
          version: demand.version,
        }).unwrap()
      }
    } catch (error) {
      let errorMessage = createErrorMessage(error)
      displayNotification({ message: `${t('message.somethingWentWrong')} ${errorMessage}`, type: 'error' })
    }
  }

  const duplicateData = async () => {
    if (!selectedDate) return

    const duplicatedWorkforceData: WorkforceScheduleModify[] = monthToDuplicateData.map(data => {
      const duplicatedData: WorkforceScheduleModify = {
        date: selectedDate.format(DateFormat.YearMonth),
        projectId: data.project.id,
        employeeId: data.employee.id,
        percent: data.percent,
        description: data.description,
        removed: false,
      }

      return duplicatedData
    })

    const fetchedData = await trigger({
      id: leaderId!!,
      props: {
        date: selectedDate.format(DateFormat.YearMonth),
      },
    })

    const removeWorkforceData: WorkforceScheduleModify[] | undefined = fetchedData.data?.map(data => {
      const removeData: WorkforceScheduleModify = {
        ...data,
        date: dayjs(data.date).format(DateFormat.YearMonth),
        projectId: data.project.id,
        employeeId: data.employee.id,
        removed: true,
      }

      return removeData
    })

    try {
      await createWorkforce({ leaderId: leaderId!!, workforceSchedules: removeWorkforceData || [] }).unwrap()
    } catch (error) {
      let errorMessage = createErrorMessage(error)
      displayNotification({ message: `${t('message.somethingWentWrong')} ${errorMessage}`, type: 'error' })
    }

    if (isSelectedDemandCopy) {
      await duplicateDemandData()
    }

    try {
      await createWorkforce({ leaderId: leaderId!!, workforceSchedules: duplicatedWorkforceData || [] }).unwrap()
      displayNotification({ message: t('page.projectDistribution.duplicatedSuccessfully'), type: 'success' })
      popupState.close()
    } catch (error) {
      let errorMessage = createErrorMessage(error)
      displayNotification({ message: `${t('page.projectDistribution.duplicateError')} ${errorMessage}`, type: 'error' })
    }
  }

  const handleConfirm = () => {
    setLoading(true)
    duplicateData().finally(() => setLoading(false))
    popupState.close()
    setIsOverwriteDataDialogOpen(false)
  }

  return (
    <>
      <Popover
        onClick={e => {
          e.stopPropagation()
        }}
        {...bindPopover(popupState)}
        anchorEl={targetRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box
          display={'flex'}
          padding={'16px'}
          borderRadius={'8px'}
          alignItems={'center'}
          gap={'8px'}
          flexDirection={'column'}
        >
          <DatePicker
            views={['month', 'year']}
            onChange={e => setSelectedDate(e)}
            value={selectedDate ? selectedDate : month.add(1, 'month')}
            label={t('page.projectDistribution.month')}
            slotProps={{ textField: { size: 'small' } }}
            minDate={dayjs().startOf('month')}
            defaultValue={month.add(1, 'month')}
            sx={{ svg: { color: 'text.mediumEmphasis' } }}
          />
          <Box display={'flex'} gap={2} alignItems={'center'}>
            <Typography>{t('page.projectDistribution.copyDemand')}</Typography>
            <Checkbox
              checked={isSelectedDemandCopy}
              onChange={e => setIsSelectedDemandCopy(e.target.checked)}
              color="primary"
              inputProps={{ 'aria-label': t('page.projectDistribution.copyDemand') }}
            />
          </Box>
          <Box display={'flex'} gap={2}>
            <ConfirmButton
              text={t('page.projectDistribution.apply')}
              onClick={() => {
                handleApplyButtonClick()
              }}
              isLoadingButton={true}
              isLoading={loading}
              isDisabled={loading}
            />
            <SquareButton
              aria-label={t('page.projectDistribution.aria.cancelDuplicateButton')}
              onClick={() => {
                popupState.close()
              }}
              size="small"
              color="secondary"
            >
              <Clear />
            </SquareButton>
          </Box>
        </Box>
      </Popover>
      <ConfirmationDialog
        open={isOverwriteDataDialogOpen}
        setOpen={setIsOverwriteDataDialogOpen}
        handleConfirm={handleConfirm}
        handleClose={() => setIsOverwriteDataDialogOpen(false)}
        title={t('components.dialog.overwriteWorkforceScheduleMonth.title')}
        content={t('components.dialog.overwriteWorkforceScheduleMonth.description')}
        loading={loading}
      />
    </>
  )
}

export default DuplicateForMonthPopup